import React, { useContext, useEffect } from 'react';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';

import {
  AntdPageHeader,
  ApplicationUIContext,
  ContentWrapper,
  Form,
  ButtonGroup,
} from '@yuhuinc/ui-library';
import { Card, Typography, Divider, Modal, message } from 'antd';
import BaseApplicationForm from './common/BaseApplicationForm';

import { phaseOneWip } from './common/actions/WipModal';
import { createApplication, resetApplicationStatuses } from './store/actions';

import { getTranslatedString } from 'helpers/i18n';
import { showFailure } from 'actions/notificationActions';
import { featureEnabled } from 'helpers/ToggleBot';

const { Title, Paragraph } = Typography;

const createApplicationText = getTranslatedString(
  'landlordPortal.applicationManagement.inviteLeadToApply.createApplication'
);

const InviteLeadToApply = ({
  disableRentForm,
  createApplication,
  showFailure,
  createApplicationSuccess,
  createApplicationFailed,
  resetApplicationStatuses,
  isDraft,
  newApplicationId,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [applicationInviteForm] = Form.useForm();
  const selectedApplicant = Form.useWatch('selectedApplicant', applicationInviteForm);
  const selectedUnit = Form.useWatch('selectedUnit', applicationInviteForm);

  useEffect(() => {
    if (createApplicationSuccess) {
      if (isDraft) {
        renderSaveApplicationSuccessModal();
      } else {
        renderSendInvitationSuccessMessage();
      }
    }
  }, [createApplicationSuccess, isDraft]);

  useEffect(() => {
    return () => resetApplicationStatuses();
  }, []);

  // There are some global styles that clashes with Application Management styles. Scoping any style with .application-management should work for now.
  // To add said scope to the highest parent element possible (ie: the <body> tag) we could use a useEffect hook that adds the scoping class after mount, and removes it after unmount.
  useEffect(() => {
    document.body.classList.add('application-management');

    return () => document.body.classList.remove('application-management');
  }, []);

  const onClickBack = () => {
    const increaseButtonSizeOnMobile = { size: isMobile ? 'large' : 'default' };

    Modal.confirm({
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.leaveApplicationCreationModal.header'
      ),
      content: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.leaveApplicationCreationModal.body'
      ),
      onOk: () => {
        resetApplicationStatuses();
        browserHistory.push('/companies/applications');
      },
      okText: getTranslatedString('okay'),
      cancelText: getTranslatedString('cancel'),
      okButtonProps: increaseButtonSizeOnMobile,
      cancelButtonProps: increaseButtonSizeOnMobile,
      bodyStyle: { fontSize: isMobile ? 16 : 14 },
    });
  };

  const onSendToApplicant = () => {
    phaseOneWip(() => {
      // TODO: Send email to applicant to complete the application and show a successful message
      Modal.warning({
        title: getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.confirmationModal.header'
        ),
        onOk: () => {
          applicationInviteForm.submit();
        },
        okText: getTranslatedString('send'),
        okCancel: true,
        cancelText: getTranslatedString('cancel'),
        content: getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.confirmationModal.body'
        ),
        destroyOnClose: true,
      });
    });
  };

  const onSaveApplicationAsDraft = () => {
    applicationInviteForm.validateFields().then((_) => {
      applicationInviteForm.setFieldsValue({ is_draft: true });
      applicationInviteForm.submit();
    });
  };

  const applicantAndUnitSelected = selectedApplicant && selectedUnit;

  const submitCompleteForm = async (fieldsValue) => {
    await createApplication(fieldsValue);
  };

  const formInitialValues = {
    is_draft: false,
    monthly_rent: selectedUnit?.market_rent,
    deposit_amount: selectedUnit?.deposit_amount || selectedUnit?.market_rent,
    key_deposit: null,
    incentives: [],
    ancillary_items: [],
    fee_templates: [],
    create_prorated_charge: true,
    lease_items: [],
    lease_start_date: null,
    lease_end_date: null,
    lease_term: null,
    application_package_id: null,
  };

  if (!featureEnabled('application-management')) {
    // Just in case someone lands on this page with the application management feature flag disabled
    browserHistory.push('/companies/applications');
  }

  const urlToRedirectOnSuccess = newApplicationId
    ? `/companies/applications/${newApplicationId}`
    : '/companies/applications';

  const renderSendInvitationSuccessMessage = () => {
    message.success(
      getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.sendInvitationsSuccess'
      )
    );

    resetApplicationStatuses();
    browserHistory.push(urlToRedirectOnSuccess);
  };

  const renderSaveApplicationSuccessModal = () =>
    Modal.success({
      title: 'Application saved',
      content: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.applicationSavedModal.body'
      ),
      onOk: () => {
        resetApplicationStatuses();
        browserHistory.push(urlToRedirectOnSuccess);
      },
      okText: getTranslatedString('okay'),
    });

  return (
    <div className="body" role="main">
      <AntdPageHeader
        onBack={onClickBack}
        title={createApplicationText}
        extra={
          <ButtonGroup
            buttons={[
              {
                label: getTranslatedString(
                  'landlordPortal.applicationManagement.inviteLeadToApply.sendInvitations'
                ),
                onClick: onSendToApplicant,
                type: 'primary',
                disabled: !applicantAndUnitSelected,
              },
              {
                label: getTranslatedString(
                  'landlordPortal.applicationManagement.inviteLeadToApply.saveApplication'
                ),
                onClick: onSaveApplicationAsDraft,
                disabled: !applicantAndUnitSelected,
              },
            ]}
            isMobile={isMobile}
          />
        }
        isMobile={isMobile}
      />
      <ContentWrapper>
        <Card
          style={{ maxWidth: 768 }}
          bodyStyle={{ padding: isMobile && 16, fontSize: isMobile && 16 }}
        >
          <Title level={isMobile ? 3 : 4}>
            {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.Title')}
          </Title>

          {!applicantAndUnitSelected && (
            <Paragraph>
              {getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.SubTitle'
              )}
            </Paragraph>
          )}

          <Divider />

          <BaseApplicationForm
            form={applicationInviteForm}
            formInitialValues={formInitialValues}
            onSubmit={submitCompleteForm}
            onError={showFailure}
            disableRentForm={disableRentForm}
            createApplicationFailed={createApplicationFailed}
            mode="create"
          />
        </Card>
      </ContentWrapper>
    </div>
  );
};

const mapStoreToProps = (store) => ({
  // Following the old flow logic (disableLeaseWizardSelections)
  // to disable the rent form section
  disableRentForm:
    store.companyDetails.companyPreferences.disable_lease_wizard_selections &&
    !store.userTypes.admin,
  createApplicationSuccess: store.landlord.applicationManagement.createApplicationSuccess,
  createApplicationFailed: store.landlord.applicationManagement.createApplicationFailed,
  isDraft: store.landlord.applicationManagement.isDraft,
  newApplicationId: store.landlord.applicationManagement.newApplicationId,
});

const mapDispatchToProps = {
  showFailure,
  createApplication,
  resetApplicationStatuses,
};

export default connect(mapStoreToProps, mapDispatchToProps)(InviteLeadToApply);
